import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { prospectsAPI, teamsV2API, usersAPI, parrainageAPI } from '@/lib/api';
import { saveProspectListContext } from '@/hooks/useReturnNavigation';
import { getCached, swrFetch, invalidatePrefix } from '@/lib/dataCache';
import { prefetchProspect } from '@/lib/resources';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { UserPickerPopover } from '@/components/common/UserPickerPopover';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    Plus, Search, Filter, Phone, Mail, MapPin, Trash2, Eye, Zap, HardHat, 
    Users, Crown, Building2, ChevronDown, ChevronRight, Clock, AlertTriangle,
    CheckCircle2, BarChart3, TrendingUp, Layers, Download, Calendar,
    CircleDollarSign, Target, CheckCircle, XCircle, Briefcase, Trophy, MessageSquareText, PackageCheck, Hash
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { syncSearchParams } from '@/lib/urlSync';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import AuditedDossiersWidget from '@/components/dashboard/AuditedDossiersWidget';
import { HashtagFilter } from '@/components/prospect/HashtagFilter';
import { HashtagChips } from '@/components/prospect/HashtagChips';
import { DeleteProspectDialog } from '@/components/prospect/DeleteProspectDialog';

// Classification des prospects
const CLASS_LABELS = {
    particulier: 'Particulier',
    professionnel: 'Professionnel',
    agri: 'Agriculture',
    tertiaire: 'Tertiaire',
    renovation: 'Rénovation'
};

const CLASS_COLORS = {
    particulier: 'bg-cyan-100 text-cyan-700',
    professionnel: 'bg-orange-100 text-orange-700',
    agri: 'bg-green-100 text-green-700',
    tertiaire: 'bg-blue-100 text-blue-700',
    renovation: 'bg-purple-100 text-purple-700'
};

// Étapes Courtage
const EASY_POWER_STAGE_NAMES = {
    ep_debut_prospection: 'Début prospection',
    ep_facture_recue: 'Facture reçue',
    ep_mandat_envoye: 'Mandat envoyé',
    ep_mandat_signe: 'Mandat signé',
    ep_dossier_transmis: 'Dossier transmis',
    ep_etude_recue: 'Étude reçue',
    ep_etude_expliquee: 'Étude expliquée',
    ep_rib_receptionne: 'RIB réceptionné',
    ep_contrat_envoye: 'Contrat envoyé',
    ep_contrat_explique: 'Attente signature',
    ep_gagne: 'Gagné',
    ep_questionnaire_chantier: 'Formulaire rempli'
};

// Étapes Travaux d’optimisation
const CHANTIER_STAGE_NAMES = {
    ch_formulaire_rempli: 'Formulaire rempli',
    ch_visite_planifiee: 'Visite planifiée',
    ch_visite_effectuee: 'Visite effectuée',
    ch_dossier_etude: 'À l\'étude',
    ch_etude_presentee: 'Étude présentée',
    ch_devis_envoye: 'Devis envoyé',
    ch_devis_accepte: 'Devis accepté',
    ch_planification: 'Planification',
    ch_en_cours: 'En cours',
    ch_termine: 'Terminé',
    ch_attente_paiement: 'Attente paiement',
    ch_paiement_effectue: 'Payé',
    ch_valide: 'Validé'
};

// Calcul du temps dans l'étape
const calculateDaysInStage = (stageChangedAt) => {
    if (!stageChangedAt) return null;
    const changed = new Date(stageChangedAt);
    const now = new Date();
    const diffMs = now - changed;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
};

// Date courte JJ/MM/AAAA
const fmtDateShort = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return null; }
};

// Options de période pour le filtre
const PERIOD_OPTIONS = [
    { value: 'all', label: 'Toutes les périodes' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: 'Cette semaine' },
    { value: 'month', label: 'Ce mois' },
    { value: 'quarter', label: 'Ce trimestre' },
    { value: 'year', label: 'Cette année' },
];

// Helper pour filtrer par période
const isInPeriod = (dateStr, period) => {
    if (!dateStr || period === 'all') return true;
    
    const date = new Date(dateStr);
    const now = new Date();
    
    switch (period) {
        case 'today':
            return date.toDateString() === now.toDateString();
        case 'week': {
            const weekAgo = new Date(now);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return date >= weekAgo;
        }
        case 'month': {
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        }
        case 'quarter': {
            const currentQuarter = Math.floor(now.getMonth() / 3);
            const dateQuarter = Math.floor(date.getMonth() / 3);
            return dateQuarter === currentQuarter && date.getFullYear() === now.getFullYear();
        }
        case 'year':
            return date.getFullYear() === now.getFullYear();
        default:
            return true;
    }
};

// Vérifier si modifié récemment (< 24h)
const isRecentlyModified = (updatedAt) => {
    if (!updatedAt) return false;
    const updated = new Date(updatedAt);
    const now = new Date();
    const diffMs = now - updated;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours < 24;
};

// Étapes "gagné" (règle officielle CRM, identique au backend : EP_WON_STAGES + CH_WON_STAGES)
const WON_STAGES = [
    'ep_gagne', 'ep_questionnaire_chantier',
    'ch_devis_accepte', 'ch_devis_signe', 'ch_dossier_subvention', 'ch_subvention_obtenue',
    'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours',
    'ch_termine', 'ch_chantier_termine',
    'ch_attente_paiement', 'ch_paiement_effectue',
    'ch_valide', 'ch_gagne',
];

// Helper pour vérifier si un prospect est en retard (>10 jours d'inactivité — règle officielle CRM)
const isProspectEnRetard = (prospect) => {
    // Un module désactivé (masqué) est considéré inexistant
    const epStage = prospect.easy_power_disabled ? null : prospect.easy_power_stage;
    const chStage = prospect.chantier_disabled ? null : prospect.chantier_stage;
    // Aucun module actif → pas en retard
    if (!epStage && !chStage) return false;
    // Un prospect perdu n'est JAMAIS en retard (il est classé)
    if (epStage === 'ep_perdu' || chStage === 'ch_perdu') {
        return false;
    }
    // Les dossiers gagnés ne sont pas non plus "en retard"
    if (WON_STAGES.includes(epStage) || WON_STAGES.includes(chStage)) {
        return false;
    }
    
    const stageUpdatedAt = prospect.stage_updated_at || prospect.updated_at;
    if (!stageUpdatedAt) return false;
    
    const updated = new Date(stageUpdatedAt);
    const now = new Date();
    const diffDays = (now - updated) / (1000 * 60 * 60 * 24);
    
    return diffDays > 10;
};

// Helper pour vérifier si un prospect est perdu (ignore les modules désactivés)
const isProspectPerdu = (prospect) => {
    const epStage = prospect.easy_power_disabled ? null : prospect.easy_power_stage;
    const chStage = prospect.chantier_disabled ? null : prospect.chantier_stage;
    return epStage === 'ep_perdu' || chStage === 'ch_perdu';
};

const isProspectGagne = (prospect) => {
    const epStage = prospect.easy_power_disabled ? null : prospect.easy_power_stage;
    const chStage = prospect.chantier_disabled ? null : prospect.chantier_stage;
    return WON_STAGES.includes(epStage) || WON_STAGES.includes(chStage);
};

// ===== Helpers SCOPÉS PAR MODULE =====
// Quand un pipeline précis est sélectionné (Courtage OU Travaux d’optimisation), les filtres de
// statut/étape doivent porter UNIQUEMENT sur ce module (et non sur l'ensemble du dossier).
const WON_STAGES_EP = ['ep_gagne', 'ep_questionnaire_chantier'];
const WON_STAGES_CH = ['ch_devis_accepte', 'ch_devis_signe', 'ch_dossier_subvention', 'ch_subvention_obtenue', 'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours', 'ch_termine', 'ch_chantier_termine', 'ch_attente_paiement', 'ch_paiement_effectue', 'ch_valide', 'ch_gagne'];
// Un module "désactivé" (masqué par l'utilisateur) n'existe plus dans les listes filtrées.
const isModuleDisabled = (p, mod) => (mod === 'chantier' ? !!p.chantier_disabled : !!p.easy_power_disabled);
const getModuleStage = (p, mod) => {
    if (isModuleDisabled(p, mod)) return null;
    return mod === 'chantier' ? p.chantier_stage : p.easy_power_stage;
};
const moduleIsPresent = (p, mod) => !!getModuleStage(p, mod);
const isModulePerdu = (p, mod) => getModuleStage(p, mod) === (mod === 'chantier' ? 'ch_perdu' : 'ep_perdu');
const isModuleGagne = (p, mod) => (mod === 'chantier' ? WON_STAGES_CH : WON_STAGES_EP).includes(getModuleStage(p, mod));
const isModuleEnRetard = (p, mod) => {
    const stage = getModuleStage(p, mod);
    if (!stage) return false;
    if (isModulePerdu(p, mod) || isModuleGagne(p, mod)) return false;
    const t = p.stage_updated_at || p.updated_at;
    if (!t) return false;
    return (new Date() - new Date(t)) / (1000 * 60 * 60 * 24) > 10;
};

// Étapes qui valident la commission Courtage
const COMMISSION_VALIDATED_STAGES_EP = ['ep_gagne', 'ep_questionnaire_chantier'];
// Étapes qui valident la commission Travaux d’optimisation
const COMMISSION_VALIDATED_STAGES_CH = ['ch_valide', 'ch_paiement_effectue'];

// Helper pour calculer le statut de la commission selon le rôle
// - `perspectiveUser` = utilisateur "point de vue". Par défaut c'est l'utilisateur
//   connecté ; mais quand la liste est filtrée par un user (drill-down profil VP
//   ou apporteur), on passe l'utilisateur cible afin d'afficher SA part et non
//   la commission totale Valnexo — c'est ça qui rend le total "Gagné en direct"
//   cohérent avec la somme des lignes.
const getCommissionStatus = (prospect, user, perspectiveUser = null) => {
    let epStatus = null;
    let chStatus = null;

    // Déterminer quelle commission afficher selon le rôle
    let epAmount = 0;
    let chAmount = 0;

    if (!user) return { epStatus: null, chStatus: null };

    // Rôle utilisé pour le calcul :
    //   - si perspectiveUser est fourni (drill-down utilisateur), on adopte son
    //     rôle sur ce dossier pour afficher SA part propre
    //   - sinon, on prend le rôle réel de l'utilisateur connecté
    const effectiveUser = perspectiveUser || user;
    const isAdmin = !perspectiveUser && (user.role === 'admin_full' || user.role === 'admin' || user.is_admin);
    const isVP = effectiveUser.role === 'vp' || (perspectiveUser && (prospect.dossier_vp_id === effectiveUser.id || prospect.dossier_vp_2_id === effectiveUser.id));
    const isApporteur = effectiveUser.role === 'apporteur' || (perspectiveUser && (prospect.dossier_apporteur_id === effectiveUser.id || prospect.dossier_apporteur_2_id === effectiveUser.id));

    if (isAdmin) {
        // Admin voit la commission Valnexo totale (vue globale)
        epAmount = prospect.ep_commission_valnexo || prospect.commission_totale_ep || 0;
        chAmount = prospect.ch_commission_valnexo || prospect.commission_totale_ch || 0;
    } else if (isVP) {
        // VP voit : sa propre commission VP + commissions des apporteurs de son équipe
        const isMyProspect = prospect.dossier_vp_id === effectiveUser.id || prospect.dossier_vp_2_id === effectiveUser.id;

        if (isMyProspect) {
            // Sa propre commission VP — payeur nette OU superviseur selon son rôle sur le dossier
            if (prospect.dossier_vp_id === effectiveUser.id) {
                // VP payeur (direct)
                epAmount = (prospect.ep_commission_vp_payeur_nette || prospect.ep_commission_vp || 0);
                chAmount = (prospect.ch_commission_vp_payeur_nette || prospect.ch_commission_vp || 0);
            } else {
                // VP superviseur uniquement (vp_2)
                epAmount = (prospect.ep_commission_vp_superviseur || 0);
                chAmount = (prospect.ch_commission_vp_superviseur || 0);
            }
        }
        // En perspective "connecté" (pas drill-down), un VP voit aussi les commissions
        // des apporteurs de son équipe sur ses dossiers. En drill-down user, on
        // n'ajoute PAS ces montants (ce sont les commissions d'un autre user).
        if (!perspectiveUser && (prospect.dossier_apporteur_id || prospect.dossier_apporteur_2_id)) {
            epAmount += (prospect.ep_commission_apporteur || 0);
            chAmount += (prospect.ch_commission_apporteur || 0);
        }
    } else if (isApporteur) {
        // Apporteur voit UNIQUEMENT SA propre commission sur SES propres dossiers
        // Il ne doit JAMAIS voir les commissions des autres apporteurs
        const isApporteur1 = prospect.dossier_apporteur_id === effectiveUser.id;
        const isApporteur2 = prospect.dossier_apporteur_2_id === effectiveUser.id;

        // STRICT: L'apporteur ne voit que les dossiers où IL est assigné comme apporteur
        if (isApporteur1) {
            // Il est apporteur principal - sa commission est dans ep_commission_apporteur_1
            epAmount = prospect.ep_commission_apporteur_1 || prospect.ep_commission_apporteur || 0;
            chAmount = prospect.ch_commission_apporteur_1 || prospect.ch_commission_apporteur || 0;
        } else if (isApporteur2) {
            // Il est apporteur secondaire - sa commission est dans ep_commission_apporteur_2
            epAmount = prospect.ep_commission_apporteur_2 || 0;
            chAmount = prospect.ch_commission_apporteur_2 || 0;
        }
        // Si l'apporteur n'est pas sur ce dossier, il ne voit rien (epAmount = 0, chAmount = 0)
    }
    
    // Vérifier si le prospect est perdu
    const isPerdu = prospect.easy_power_stage === 'ep_perdu' || prospect.chantier_stage === 'ch_perdu';
    
    // Commission EP
    if (epAmount > 0) {
        const isValidated = COMMISSION_VALIDATED_STAGES_EP.includes(prospect.easy_power_stage);
        epStatus = {
            amount: epAmount,
            validated: isValidated,
            perdu: prospect.easy_power_stage === 'ep_perdu',
            type: 'EP'
        };
    }
    
    // Commission Travaux d’optimisation
    if (chAmount > 0) {
        const isValidated = COMMISSION_VALIDATED_STAGES_CH.includes(prospect.chantier_stage);
        chStatus = {
            amount: chAmount,
            validated: isValidated,
            perdu: prospect.chantier_stage === 'ch_perdu',
            type: 'CH'
        };
    }
    
    return { epStatus, chStatus, isPerdu };
};

// Vérifier si l'utilisateur peut voir la commission d'un prospect
const canViewCommission = (user, prospect) => {
    if (!user) return false;
    
    // Admin Full voit tout
    if (user.role === 'admin_full' || user.role === 'admin' || user.is_admin) return true;
    
    // VP voit ses prospects et ceux de ses apporteurs
    if (user.role === 'vp') {
        // Prospect attribué au VP
        if (prospect.dossier_vp_id === user.id || prospect.dossier_vp_2_id === user.id) return true;
        // Prospect attribué à un de ses apporteurs (le VP est le "parent")
        if (prospect.assigned_to_vp_id === user.id) return true;
        // Prospect dans une de ses équipes
        if (user.team_ids?.includes(prospect.team_id)) return true;
        return true; // VP voit tout (ses apporteurs sont dans son scope)
    }
    
    // Apporteur ne voit que ses propres prospects
    if (user.role === 'apporteur') {
        return prospect.dossier_apporteur_id === user.id || 
               prospect.dossier_apporteur_2_id === user.id ||
               prospect.assigned_to === user.id;
    }
    
    return false;
};

// Composant pour afficher la commission
// `perspectiveUser` optionnel : quand la liste est filtrée par un user (drill-down
// profil VP / apporteur), on adopte sa perspective pour afficher SA part propre
// au lieu de la commission Valnexo totale — ainsi la somme des lignes correspond
// aux widgets "Gagné en direct" du profil.
const CommissionCell = ({ prospect, user, perspectiveUser = null }) => {
    const { epStatus, chStatus } = getCommissionStatus(prospect, user, perspectiveUser);
    
    if (!epStatus && !chStatus) {
        return <span className="text-xs text-slate-400">-</span>;
    }
    
    const formatAmount = (amount) => {
        if (!amount || amount === 0) return null;
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
    };
    
    // Déterminer le style et le label selon le statut
    const getStatusStyle = (status) => {
        if (status.perdu) {
            // Commission perdue = grisé/barré
            return "bg-slate-100 text-slate-400 line-through";
        } else if (status.validated) {
            // Commission validée = vert
            return "bg-emerald-100 text-emerald-700";
        } else {
            // Commission à aller chercher = orange/bleu selon type
            return status.type === 'EP' 
                ? "bg-amber-50 text-amber-700 border border-amber-200"
                : "bg-blue-50 text-blue-700 border border-blue-200";
        }
    };
    
    const getStatusLabel = (status) => {
        if (status.perdu) return 'perdue';
        if (status.validated) return 'validée';
        return 'à aller chercher';
    };
    
    return (
        <div className="space-y-1">
            {epStatus && epStatus.amount > 0 && (
                <div className={cn(
                    "flex items-center gap-1.5 text-xs px-2 py-1 rounded",
                    getStatusStyle(epStatus)
                )}>
                    <Zap className="w-3 h-3" />
                    <span className="font-medium">{formatAmount(epStatus.amount)}</span>
                    <span className="text-[10px]">
                        {getStatusLabel(epStatus)}
                    </span>
                </div>
            )}
            {chStatus && chStatus.amount > 0 && (
                <div className={cn(
                    "flex items-center gap-1.5 text-xs px-2 py-1 rounded",
                    getStatusStyle(chStatus)
                )}>
                    <HardHat className="w-3 h-3" />
                    <span className="font-medium">{formatAmount(chStatus.amount)}</span>
                    <span className="text-[10px]">
                        {getStatusLabel(chStatus)}
                    </span>
                </div>
            )}
        </div>
    );
};

const getStageDisplay = (prospect) => {
    const stages = [];
    
    if (prospect.easy_power_stage) {
        const label = EASY_POWER_STAGE_NAMES[prospect.easy_power_stage] || prospect.easy_power_stage;
        const isWon = prospect.easy_power_stage === 'ep_gagne' || prospect.easy_power_stage === 'ep_questionnaire_chantier';
        const daysInStage = calculateDaysInStage(prospect.easy_power_stage_changed_at);
        stages.push({
            type: 'easy_power',
            label: label,
            color: isWon ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700',
            icon: Zap,
            daysInStage,
            stageChangedAt: prospect.easy_power_stage_changed_at,
            isWarning: daysInStage !== null && daysInStage > 5 && !isWon
        });
    }
    
    if (prospect.chantier_stage) {
        const label = CHANTIER_STAGE_NAMES[prospect.chantier_stage] || prospect.chantier_stage;
        const isValidated = prospect.chantier_stage === 'ch_valide';
        const daysInStage = calculateDaysInStage(prospect.chantier_stage_changed_at);
        stages.push({
            type: 'chantier',
            label: label,
            color: isValidated ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700',
            icon: HardHat,
            daysInStage,
            stageChangedAt: prospect.chantier_stage_changed_at,
            isWarning: daysInStage !== null && daysInStage > 5 && !isValidated
        });
    }
    
    return stages;
};

// Composant pour afficher le temps dans l'étape
const DaysInStageBadge = ({ days, isWarning }) => {
    if (days === null) return null;
    
    return (
        <span className={cn(
            "text-xs ml-1 px-1.5 py-0.5 rounded",
            isWarning 
                ? "bg-red-100 text-red-700 font-semibold" 
                : "bg-slate-100 text-slate-600"
        )}>
            {days}j
            {isWarning && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}
        </span>
    );
};

// Composant Stats Card
const STAT_COLORS = {
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
};

const StatsCard = ({ title, value, icon: Icon, trend, color = "slate" }) => {
    const c = STAT_COLORS[color] || STAT_COLORS.slate;
    return (
    <Card className="bg-white">
        <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
                    {trend && <p className="text-xs text-green-600 mt-1">{trend}</p>}
                </div>
                <div className={cn("p-3 rounded-lg", c.bg)}>
                    <Icon className={cn("w-5 h-5", c.text)} />
                </div>
            </div>
        </CardContent>
    </Card>
    );
};

export default function Prospects() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, wallUnreadItems } = useAuth();
    // Map dossier -> nombre de messages non lus (réutilise la synthèse du contexte, aucune requête)
    const wallUnreadByProspect = (wallUnreadItems || []).reduce((acc, it) => {
        acc[it.prospect_id] = (acc[it.prospect_id] || 0) + (it.unread_count || 0);
        return acc;
    }, {});
    const wallUnreadTotal = Object.values(wallUnreadByProspect).reduce((a, b) => a + b, 0);
    const [prospects, setProspects] = useState([]);
    const [filteredProspects, setFilteredProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Initialiser les filtres depuis l'URL ou valeurs par défaut
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [classFilter, setClassFilter] = useState(searchParams.get('class') || 'all');
    const [teamFilter, setTeamFilter] = useState(searchParams.get('team') || 'all');
    const [userFilter, setUserFilter] = useState(searchParams.get('user') || 'all');
    const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || 'all');
    const [pipelineFilter, setPipelineFilter] = useState(searchParams.get('pipeline') || 'all');
    const [periodFilter, setPeriodFilter] = useState(searchParams.get('period') || 'all');
    const [commissionFilter, setCommissionFilter] = useState(searchParams.get('commission') || 'all');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
    // Filtre "Périmètre" (VP superviseur) : all | mine | supervised
    // - mine       : dossiers dont je suis VP payeur / VP2 direct ou apporteur ⇒ mon équipe
    // - supervised : dossiers des équipes que je supervise (via team.supervisor_vp_id)
    const [scopeFilter, setScopeFilter] = useState(searchParams.get('scope') || 'all');
    const [hashtagFilter, setHashtagFilter] = useState(
        (searchParams.get('hashtags') || '').split(',').filter(Boolean)
    );
    // Filtres Parrainage (admin & VP avec module activé)
    const [originFilter, setOriginFilter] = useState(searchParams.get('origin') || 'all');
    const [parrainLevelFilter, setParrainLevelFilter] = useState(searchParams.get('plevel') || 'all');
    const [parrainFilter, setParrainFilter] = useState(searchParams.get('parrain') || 'all');
    const [parrainsList, setParrainsList] = useState([]);
    const [quickTab, setQuickTab] = useState(searchParams.get('view') || 'all'); // all | en_cours | gagnes | en_retard
    const [viewMode, setViewMode] = useState(searchParams.get('viewmode') || 'list'); // 'list' ou 'grouped'
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [expandedTeams, setExpandedTeams] = useState({});
    const [expandedUsers, setExpandedUsers] = useState({});
    const [showStats, setShowStats] = useState(true);
    
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin;
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    // Filtres Parrainage visibles : admins + tous les VP (module activé par défaut).
    const showParrainageFilters = isAdmin || isVP;

    useEffect(() => {
        if (!showParrainageFilters) return;
        parrainageAPI.listParrains()
            .then(res => setParrainsList(Array.isArray(res.data) ? res.data : (res.data?.parrains || [])))
            .catch(() => setParrainsList([]));
    }, [showParrainageFilters]);

    // Synchroniser les filtres avec l'URL
    useEffect(() => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (classFilter !== 'all') params.set('class', classFilter);
        if (teamFilter !== 'all') params.set('team', teamFilter);
        if (userFilter !== 'all') params.set('user', userFilter);
        if (stageFilter !== 'all') params.set('stage', stageFilter);
        if (pipelineFilter !== 'all') params.set('pipeline', pipelineFilter);
        if (periodFilter !== 'all') params.set('period', periodFilter);
        if (commissionFilter !== 'all') params.set('commission', commissionFilter);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (scopeFilter !== 'all') params.set('scope', scopeFilter);
        if (hashtagFilter.length > 0) params.set('hashtags', hashtagFilter.join(','));
        if (originFilter !== 'all') params.set('origin', originFilter);
        if (parrainLevelFilter !== 'all') params.set('plevel', parrainLevelFilter);
        if (parrainFilter !== 'all') params.set('parrain', parrainFilter);
        if (quickTab !== 'all') params.set('view', quickTab);
        if (viewMode !== 'list') params.set('viewmode', viewMode);
        // Préserver le filtre par IDs (ex. « À relancer ») qui n'est pas géré par un state local
        const idsParam = searchParams.get('ids');
        if (idsParam) params.set('ids', idsParam);
        const labelParam = searchParams.get('label');
        if (labelParam) params.set('label', labelParam);
        
        // Mettre à jour l'URL sans navigation (et sans churn redondant au montage)
        syncSearchParams(setSearchParams, searchParams, params);
    }, [search, classFilter, teamFilter, userFilter, stageFilter, pipelineFilter, periodFilter, commissionFilter, statusFilter, scopeFilter, hashtagFilter, originFilter, parrainLevelFilter, parrainFilter, quickTab, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterProspects();
    }, [prospects, search, classFilter, teamFilter, userFilter, stageFilter, pipelineFilter, periodFilter, commissionFilter, statusFilter, scopeFilter, hashtagFilter, originFilter, parrainLevelFilter, parrainFilter, quickTab, searchParams.get('ids')]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        const key = `prospects:list:${user?.id}:${user?.role}`;
        const cached = getCached(key);
        if (cached) {
            setProspects(cached.prospects);
            setTeams(cached.teams);
            setUsers(cached.users);
            setLoading(false);
        } else {
            setLoading(true);
        }
        try {
            const data = await swrFetch(key, async () => {
                const [prospectsRes, teamsRes, usersRes] = await Promise.all([
                    prospectsAPI.getAll({ page_size: 100000 }, { timeout: 60000 }),
                    isAdmin || isVP ? teamsV2API.getAll() : Promise.resolve({ data: [] }),
                    // Admin voit TOUS les users ; VP voit uniquement les apporteurs
                    // de son équipe pour pouvoir filtrer ses dossiers par apporteur.
                    (isAdmin || isVP) ? usersAPI.getAll() : Promise.resolve({ data: [] })
                ]);
                const rawUsers = usersRes.data || [];
                let scopedUsers;
                if (isAdmin) {
                    scopedUsers = rawUsers.filter(u => u.is_active && ['vp', 'apporteur'].includes(u.role));
                } else if (isVP) {
                    // Un VP filtre uniquement sur ses propres apporteurs
                    // (vp_id === user.id après la réconciliation team).
                    scopedUsers = rawUsers.filter(u => u.is_active && u.role === 'apporteur' && u.vp_id === user.id);
                } else {
                    scopedUsers = [];
                }
                return {
                    prospects: prospectsRes.data?.prospects || prospectsRes.data || [],
                    teams: teamsRes.data || [],
                    users: scopedUsers,
                };
            });
            setProspects(data.prospects);
            setTeams(data.teams);
            setUsers(data.users);
        } catch (error) {
            if (!cached) {
                console.error('Error loading data:', error);
                toast.error('Erreur lors du chargement');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterProspects = () => {
        let filtered = [...prospects];

        // Filtre par liste d'IDs (ex. « À relancer » depuis Impact Score) — snapshot précis
        const idsParam = searchParams.get('ids');
        if (idsParam) {
            const idSet = new Set(idsParam.split(',').filter(Boolean));
            filtered = filtered.filter(p => idSet.has(p.id));
        }

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(p => 
                p.first_name?.toLowerCase().includes(searchLower) ||
                p.last_name?.toLowerCase().includes(searchLower) ||
                p.company_name?.toLowerCase().includes(searchLower) ||
                p.email?.toLowerCase().includes(searchLower) ||
                p.phone?.includes(search) ||
                p.city?.toLowerCase().includes(searchLower)
            );
        }
        
        if (classFilter !== 'all') {
            filtered = filtered.filter(p => p.prospect_class === classFilter);
        }
        
        if (teamFilter !== 'all') {
            filtered = filtered.filter(p => p.team_id === teamFilter);
        }

        // Filtre "Périmètre" pour les VP :
        //   supervised = dossier dont l'équipe (team_id) me liste comme supervisor_vp_id
        //                mais où je NE SUIS PAS payer_vp_id → dossier d'une équipe supervisée
        //   mine       = tous les autres dossiers (mon équipe payeur, mes attributions directes)
        //   vp_direct  = dossiers que je gère seul (pas d'apporteur, pas de parrain) → VP direct
        if (isVP && scopeFilter !== 'all') {
            if (scopeFilter === 'vp_direct') {
                filtered = filtered.filter(p =>
                    (p.dossier_vp_id === user.id || p.dossier_vp_2_id === user.id) &&
                    !p.dossier_apporteur_id &&
                    !p.dossier_apporteur_2_id &&
                    p.origin !== 'parrainage'
                );
            } else {
                const supervisedTeamIds = new Set(
                    (teams || [])
                        .filter(t => t.supervisor_vp_id === user.id && t.payer_vp_id !== user.id)
                        .map(t => t.id)
                );
                filtered = filtered.filter(p => {
                    const isSupervised = supervisedTeamIds.has(p.team_id);
                    return scopeFilter === 'supervised' ? isSupervised : !isSupervised;
                });
            }
        }
        
        if (userFilter !== 'all') {
            // Aligné sur le backend `/users/{id}/profile` : on inclut aussi les VP
            // secondaires (dossier_vp_2_id) et les apporteurs secondaires
            // (dossier_apporteur_2_id). Sinon les compteurs du header VP ne
            // correspondaient pas au drill-down (dossiers manquants en superviseur).
            filtered = filtered.filter(p =>
                p.dossier_apporteur_id === userFilter ||
                p.dossier_apporteur_2_id === userFilter ||
                p.dossier_vp_id === userFilter ||
                p.dossier_vp_2_id === userFilter ||
                p.assigned_to === userFilter
            );
        }
        
        // Module ciblé par le pipeline (null = "Tous" → logique large sur les 2 modules)
        const scopeMod = pipelineFilter === 'easy_power' ? 'easy_power'
            : pipelineFilter === 'chantier' ? 'chantier' : null;
        
        // Filtre par pipeline (Courtage / Travaux d’optimisation / Perdus)
        if (pipelineFilter !== 'all') {
            if (scopeMod) {
                // On garde les dossiers CONCERNÉS par ce module (module présent).
                // L'inclusion/exclusion des perdus est gérée par les filtres de statut/étape,
                // pour que "Travaux d’optimisation + Perdus" = "le module Travaux d’optimisation est perdu".
                filtered = filtered.filter(p => moduleIsPresent(p, scopeMod));
            } else if (pipelineFilter === 'lost') {
                filtered = filtered.filter(p => p.easy_power_stage === 'ep_perdu' || p.chantier_stage === 'ch_perdu');
            }
        }
        
        // Filtre par étape spécifique (scopé au module sélectionné)
        if (stageFilter !== 'all') {
            filtered = filtered.filter(p =>
                scopeMod
                    ? getModuleStage(p, scopeMod) === stageFilter
                    : (p.easy_power_stage === stageFilter || p.chantier_stage === stageFilter)
            );
        }
        
        // Filtre par période — choix du champ de date COHÉRENT avec le dashboard :
        // - Onglet En cours / En retard / Attente installation : dernier changement d'étape
        //   (idem dashboard), pour ne pas exclure les dossiers anciens dont le statut évolue.
        // - Onglet Gagnés : géré plus bas dans le bloc quickTab (via stage_changed_at).
        // - Autres cas (Tous) : date de création du dossier (comportement historique).
        if (periodFilter !== 'all' && quickTab !== 'gagnes') {
            if (quickTab === 'en_cours' || quickTab === 'en_retard' || quickTab === 'attente_installation') {
                filtered = filtered.filter(p => (
                    isInPeriod(p.easy_power_stage_changed_at, periodFilter) ||
                    isInPeriod(p.chantier_stage_changed_at, periodFilter)
                ));
            } else {
                filtered = filtered.filter(p => isInPeriod(p.created_at, periodFilter));
            }
        }
        
        // Filtre par statut de commission
        if (commissionFilter !== 'all') {
            filtered = filtered.filter(p => {
                const { epStatus, chStatus } = getCommissionStatus(p, user);
                const hasCommission = (epStatus?.amount > 0) || (chStatus?.amount > 0);
                
                if (commissionFilter === 'a_chercher') {
                    // Commission à aller chercher (non validée ET non perdue)
                    const epAChercher = epStatus?.amount > 0 && !epStatus?.validated && !epStatus?.perdu;
                    const chAChercher = chStatus?.amount > 0 && !chStatus?.validated && !chStatus?.perdu;
                    return epAChercher || chAChercher;
                } else if (commissionFilter === 'validee') {
                    // Commission validée
                    const epValidee = epStatus?.amount > 0 && epStatus?.validated;
                    const chValidee = chStatus?.amount > 0 && chStatus?.validated;
                    return epValidee || chValidee;
                } else if (commissionFilter === 'avec_commission') {
                    // Tous les dossiers avec une commission
                    return hasCommission;
                } else if (commissionFilter === 'sans_commission') {
                    // Dossiers sans commission
                    return !hasCommission;
                }
                return true;
            });
        }
        
        // Filtre par statut (en retard, perdu, actif) — scopé au module sélectionné
        if (statusFilter !== 'all') {
            filtered = filtered.filter(p => {
                if (statusFilter === 'en_retard') {
                    return scopeMod ? isModuleEnRetard(p, scopeMod) : isProspectEnRetard(p);
                } else if (statusFilter === 'perdu') {
                    return scopeMod ? isModulePerdu(p, scopeMod) : isProspectPerdu(p);
                } else if (statusFilter === 'actif') {
                    // Actifs = ni perdus ni en retard (sur le module ciblé si pipeline sélectionné)
                    return scopeMod
                        ? (!isModulePerdu(p, scopeMod) && !isModuleEnRetard(p, scopeMod))
                        : (!isProspectPerdu(p) && !isProspectEnRetard(p));
                }
                return true;
            });
        }
        
        // Filtre par hashtags # (logique "OU"), aligné sur le pipeline sélectionné.
        // Fix 22/02/2026 : n'inclut le dossier QUE si son module portant le hashtag
        // est réellement actif (stage défini). Sinon on remonte des orphelins
        // invisibles sur la fiche → incohérence liste vs détail (bug Jérémy).
        if (hashtagFilter.length > 0) {
            filtered = filtered.filter(p => {
                const ep = p.hashtags_easy_power || [];
                const ch = p.hashtags_chantier || [];
                const epActive = !!p.easy_power_stage;
                const chActive = !!p.chantier_stage;
                const epMatch = epActive && hashtagFilter.some(id => ep.includes(id));
                const chMatch = chActive && hashtagFilter.some(id => ch.includes(id));
                if (pipelineFilter === 'easy_power') return epMatch;
                if (pipelineFilter === 'chantier') return chMatch;
                return epMatch || chMatch;
            });
        }

        // ===== Filtres Parrainage =====
        if (originFilter === 'parrainage') {
            filtered = filtered.filter(p => p.origin === 'parrainage');
        } else if (originFilter === 'apporteur') {
            filtered = filtered.filter(p => p.origin !== 'parrainage');
        }
        if (parrainLevelFilter !== 'all') {
            const lvl = parseInt(parrainLevelFilter, 10);
            filtered = filtered.filter(p => (p.parrainage_snapshot?.parrain_apporteur_level) === lvl);
        }
        if (parrainFilter !== 'all') {
            filtered = filtered.filter(p => (p.parrainage_snapshot?.recipients || []).some(r => r.parrain_id === parrainFilter));
        }

        // QuickTab : En cours / En attente d'installation / Gagnés / En retard (vue rapide depuis dashboard)
        if (quickTab !== 'all') {
            const FINAL_LOST = ['ep_perdu', 'ch_perdu'];
            const WON_EP = ['ep_gagne', 'ep_questionnaire_chantier'];
            // Travaux d’optimisation "en attente d'installation" : devis accepté/signé → avant "Travaux d’optimisation terminé"
            const AWAITING_CH = [
                'ch_devis_accepte', 'ch_devis_signe',
                'ch_dossier_subvention', 'ch_subvention_obtenue',
                'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours',
            ];
            // CH gagné = à partir de "devis accepté" (toutes étapes ultérieures incluses)
            const WON_CH = [
                'ch_devis_accepte', 'ch_devis_signe',
                'ch_dossier_subvention', 'ch_subvention_obtenue',
                'ch_chantier_en_cours', 'ch_planification',
                'ch_chantier_termine', 'ch_paiement_effectue',
                'ch_valide', 'ch_gagne',
            ];
            const ALL_FINAL = [...FINAL_LOST, ...WON_EP, ...WON_CH];
            filtered = filtered.filter(p => {
                // Un module désactivé (easy_power_disabled / chantier_disabled) est considéré
                // comme inexistant : son stage ne compte pas pour le filtrage.
                const epStage = p.easy_power_disabled ? null : p.easy_power_stage;
                const chStage = p.chantier_disabled ? null : p.chantier_stage;
                if (quickTab === 'en_cours') {
                    // Doit avoir au moins un module ACTIF en cours (ni gagné, ni perdu, ni awaiting)
                    const epActiveInProgress = epStage && !ALL_FINAL.includes(epStage);
                    const chActiveInProgress = chStage && !ALL_FINAL.includes(chStage) && !AWAITING_CH.includes(chStage);
                    return epActiveInProgress || chActiveInProgress;
                } else if (quickTab === 'attente_installation') {
                    return AWAITING_CH.includes(chStage);
                } else if (quickTab === 'gagnes') {
                    const isWonEP = WON_EP.includes(epStage);
                    const isWonCH = WON_CH.includes(chStage);
                    if (!isWonEP && !isWonCH) return false;
                    // Si filtre période actif, on filtre sur la date du gain (stage_changed_at)
                    if (periodFilter !== 'all') {
                        const wonDate = isWonEP ? p.easy_power_stage_changed_at : p.chantier_stage_changed_at;
                        return wonDate ? isInPeriod(wonDate, periodFilter) : false;
                    }
                    return true;
                } else if (quickTab === 'en_retard') {
                    return isProspectEnRetard(p);
                } else if (quickTab === 'unread') {
                    // Onglet virtuel : uniquement les dossiers ayant des messages non lus
                    return (wallUnreadByProspect[p.id] || 0) > 0;
                }
                return true;
            });
        }
        
        setFilteredProspects(filtered);
    };

    // Grouper les prospects par équipe puis par utilisateur
    const groupedData = useMemo(() => {
        const grouped = {};
        
        filteredProspects.forEach(prospect => {
            const teamId = prospect.team_id || (prospect.is_vp_direct ? 'vp_direct' : 'no_team');
            const teamName = prospect.team_name || (prospect.is_vp_direct ? '🎯 VP Direct' : 'Sans équipe');
            
            if (!grouped[teamId]) {
                grouped[teamId] = {
                    name: teamName,
                    users: {},
                    totalProspects: 0,
                    warningCount: 0
                };
            }
            
            // Déterminer l'utilisateur responsable (apporteur ou VP)
            const userId = prospect.dossier_apporteur_id || prospect.dossier_vp_id || 'no_user';
            const userName = prospect.dossier_apporteur_name || prospect.dossier_vp_name || 'Non assigné';
            const userRole = prospect.dossier_apporteur_id ? 'apporteur' : (prospect.dossier_vp_id ? 'vp' : 'none');
            
            if (!grouped[teamId].users[userId]) {
                grouped[teamId].users[userId] = {
                    name: userName,
                    role: userRole,
                    prospects: [],
                    warningCount: 0
                };
            }
            
            // Vérifier si le prospect a un warning
            const stages = getStageDisplay(prospect);
            const hasWarning = stages.some(s => s.isWarning);
            
            grouped[teamId].users[userId].prospects.push({
                ...prospect,
                stages,
                hasWarning,
                isRecent: isRecentlyModified(prospect.updated_at)
            });
            
            grouped[teamId].totalProspects++;
            if (hasWarning) {
                grouped[teamId].warningCount++;
                grouped[teamId].users[userId].warningCount++;
            }
        });
        
        return grouped;
    }, [filteredProspects]);

    // Compteur dédié au filtre # : nombre de dossiers portant le(s) hashtag(s)
    // sélectionné(s) (indépendant des autres filtres), aligné sur le pipeline choisi.
    // Applique la MÊME règle module-actif que le filtre principal ci-dessus.
    const hashtagMatchCount = useMemo(() => {
        if (hashtagFilter.length === 0) return 0;
        return prospects.filter(p => {
            const ep = p.hashtags_easy_power || [];
            const ch = p.hashtags_chantier || [];
            const epActive = !!p.easy_power_stage;
            const chActive = !!p.chantier_stage;
            const epMatch = epActive && hashtagFilter.some(id => ep.includes(id));
            const chMatch = chActive && hashtagFilter.some(id => ch.includes(id));
            if (pipelineFilter === 'easy_power') return epMatch;
            if (pipelineFilter === 'chantier') return chMatch;
            return epMatch || chMatch;
        }).length;
    }, [prospects, hashtagFilter, pipelineFilter]);

    // Calculer les stats
    const stats = useMemo(() => {
        const scopeMod = pipelineFilter === 'easy_power' ? 'easy_power'
            : pipelineFilter === 'chantier' ? 'chantier' : null;
        const total = filteredProspects.length;
        let warningCount = 0;
        let recentCount = 0;
        let perdusCount = 0;
        let gagnesCount = 0;
        const stageCount = {};
        
        filteredProspects.forEach(p => {
            const stages = getStageDisplay(p);
            stages.forEach(s => {
                stageCount[s.label] = (stageCount[s.label] || 0) + 1;
            });
            // En retard / perdu / gagné : scopés au module si un pipeline est sélectionné
            const enRetard = scopeMod ? isModuleEnRetard(p, scopeMod) : isProspectEnRetard(p);
            const perdu = scopeMod ? isModulePerdu(p, scopeMod) : isProspectPerdu(p);
            const gagne = scopeMod ? isModuleGagne(p, scopeMod) : isProspectGagne(p);
            if (enRetard) warningCount++;
            if (isRecentlyModified(p.updated_at)) recentCount++;
            // Partition additive (priorité : perdu > gagné > en retard > en cours)
            if (perdu) perdusCount++;
            else if (gagne) gagnesCount++;
        });
        
        // En cours = le reste (ni perdu, ni gagné, ni en retard) → Total = enCours + enRetard + gagnés + perdus
        const enCoursCount = Math.max(0, total - perdusCount - gagnesCount - warningCount);
        // Taux de conversion (dossiers gagnés / total)
        const conversionRate = total > 0 ? Math.round((gagnesCount / total) * 100) : 0;
        
        return { total, warningCount, recentCount, conversionRate, stageCount, perdusCount, gagnesCount, enCoursCount };
    }, [filteredProspects, pipelineFilter]);

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        const target = prospects.find(p => p.id === id);
        if (!target) return;
        setDeleteDialog({ open: true, prospect: target });
    };

    const [deleteDialog, setDeleteDialog] = useState({ open: false, prospect: null });
    const [deleteProcessing, setDeleteProcessing] = useState(false);

    const prospectDisplayName = (p) =>
        p?.company_name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim() || p?.raison_sociale || '';

    const confirmDelete = async (confirmName) => {
        const target = deleteDialog.prospect;
        if (!target) return;
        try {
            setDeleteProcessing(true);
            await prospectsAPI.delete(target.id, confirmName);
            setProspects(prev => prev.filter(p => p.id !== target.id));
            invalidatePrefix('prospects');
            invalidatePrefix('dashboard');
            invalidatePrefix('pilotage');
            toast.success('Dossier mis à la corbeille');
            setDeleteDialog({ open: false, prospect: null });
        } catch (error) {
            const detail = error?.response?.data?.detail || 'Erreur lors de la suppression';
            toast.error(detail);
        } finally {
            setDeleteProcessing(false);
        }
    };

    const toggleTeam = (teamId) => {
        setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
    };

    const toggleUser = (teamId, userId) => {
        const key = `${teamId}-${userId}`;
        setExpandedUsers(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Composant ligne de prospect
    const ProspectRow = ({ prospect, compact = false, listContext = null }) => {
        // Le dossier vient-il d'une équipe que JE SUPERVISE (et où je ne suis pas payeur) ?
        // Sert au badge "Supervisé" affiché aux VP superviseurs.
        const isSupervisedByMe = isVP && (() => {
            const t = (teams || []).find(x => x.id === prospect.team_id);
            return !!t && t.supervisor_vp_id === user?.id && t.payer_vp_id !== user?.id;
        })();
        const supervisedTeamName = isSupervisedByMe
            ? (teams.find(x => x.id === prospect.team_id)?.name || '')
            : '';
        const handleRowClick = () => {
            // Sauvegarde la liste courante (ordre affiché) pour la navigation Prev/Next
            try {
                const ids = (listContext?.ids && listContext.ids.length ? listContext.ids : filteredProspects.map(p => p.id));
                saveProspectListContext(ids, prospect.id, {
                    path: window.location.pathname + window.location.search,
                    label: 'Retour à la liste',
                });
            } catch { /* noop */ }
            navigate(`/prospects/${prospect.id}`);
        };
        const stages = prospect.stages || getStageDisplay(prospect);
        const isRecent = prospect.isRecent ?? isRecentlyModified(prospect.updated_at);
        const hasWarning = prospect.hasWarning ?? stages.some(s => s.isWarning);
        
        return (
            <TableRow
                className={cn(
                    "cursor-pointer hover:bg-slate-50 transition-colors",
                    hasWarning && "bg-red-50/50",
                    isRecent && "border-l-4 border-l-green-500"
                )}
                onClick={handleRowClick}
                onMouseEnter={() => prefetchProspect(prospect.id)}
                data-testid={`prospect-row-${prospect.id}`}
            >
                <TableCell>
                    <div className="flex items-center gap-3">
                        {isRecent && (
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" title="Modifié récemment" />
                        )}
                        {hasWarning && !isRecent && (
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" title="En retard" />
                        )}
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <span className="text-sm font-semibold text-slate-600">
                                {prospect.first_name?.[0]}{prospect.last_name?.[0]}
                            </span>
                        </div>
                        <div>
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                                {prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}
                                {prospect.origin === 'parrainage' && (
                                    <Badge className="bg-indigo-100 text-indigo-700 text-[10px] font-semibold" data-testid={`row-parrainage-badge-${prospect.id}`}>
                                        PARRAINAGE N{prospect.parrainage_snapshot?.parrain_apporteur_level || ''}
                                    </Badge>
                                )}
                                {isSupervisedByMe && (
                                    <Badge
                                        className="bg-amber-100 text-amber-800 text-[10px] font-semibold border border-amber-300"
                                        title={`Dossier de l'équipe supervisée${supervisedTeamName ? ' : ' + supervisedTeamName : ''}`}
                                        data-testid={`row-supervised-badge-${prospect.id}`}
                                    >
                                        <Users className="w-3 h-3 mr-1" />
                                        SUPERVISÉ
                                    </Badge>
                                )}
                                {wallUnreadByProspect[prospect.id] > 0 && (
                                    <span
                                        data-testid={`row-wall-badge-${prospect.id}`}
                                        title={(() => {
                                            const items = (wallUnreadItems || []).filter((it) => it.prospect_id === prospect.id);
                                            const parts = items.map((it) => `${it.unread_count} sur ${it.module_label}`);
                                            return `${wallUnreadByProspect[prospect.id]} message${wallUnreadByProspect[prospect.id] > 1 ? 's' : ''} non lu${wallUnreadByProspect[prospect.id] > 1 ? 's' : ''}${parts.length > 0 ? ' · ' + parts.join(' · ') : ''}`;
                                        })()}
                                        className="inline-flex items-center gap-1 min-w-[20px] h-5 px-1.5 rounded-full bg-purple-600 text-white text-[10px] font-bold leading-none"
                                    >
                                        <MessageSquareText className="w-3 h-3" />
                                        {wallUnreadByProspect[prospect.id] > 99 ? '99+' : wallUnreadByProspect[prospect.id]}
                                    </span>
                                )}
                            </div>
                            {prospect.company_name && (
                                <p className="text-sm text-slate-500">
                                    {prospect.first_name} {prospect.last_name}
                                </p>
                            )}
                            <HashtagChips
                                easyPower={prospect.hashtags_easy_power}
                                chantier={prospect.hashtags_chantier}
                                className="mt-1"
                                onTagClick={(id) => setHashtagFilter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
                            />
                        </div>
                    </div>
                </TableCell>
                {!compact && (
                    <>
                        <TableCell>
                            <div className="space-y-1">
                                {prospect.phone && (
                                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                        <Phone className="w-3 h-3" />
                                        {prospect.phone}
                                    </div>
                                )}
                                {prospect.email && (
                                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                        <Mail className="w-3 h-3" />
                                        {prospect.email}
                                    </div>
                                )}
                            </div>
                        </TableCell>
                        <TableCell>
                            {prospect.city && (
                                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                    <MapPin className="w-3 h-3" />
                                    {prospect.city}
                                </div>
                            )}
                        </TableCell>
                    </>
                )}
                <TableCell>
                    <div className="flex flex-col gap-1.5">
                        {stages.length === 0 ? (
                            <span className="text-sm text-slate-400">-</span>
                        ) : (
                            stages.map((stage) => {
                                const StageIcon = stage.icon;
                                return (
                                    <div key={stage.type} className="flex flex-col gap-0.5">
                                        <div className="flex items-center flex-wrap">
                                            <Badge className={cn(stage.color, "text-xs", stage.isWarning && "ring-2 ring-red-400")}>
                                                <StageIcon className="w-3 h-3 mr-1" />
                                                {stage.label}
                                            </Badge>
                                            <DaysInStageBadge days={stage.daysInStage} isWarning={stage.isWarning} />
                                        </div>
                                        {stage.stageChangedAt && (
                                            <span className="text-[10px] text-slate-400 leading-tight">
                                                Sur cette étape depuis le {fmtDateShort(stage.stageChangedAt)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })
                        )}
                        {prospect.created_at && (
                            <span className="text-[10px] text-slate-400 leading-tight border-t border-slate-100 pt-1 mt-0.5">
                                Dossier créé le {fmtDateShort(prospect.created_at)}
                                {calculateDaysInStage(prospect.created_at) != null && ` · il y a ${calculateDaysInStage(prospect.created_at)}j`}
                            </span>
                        )}
                    </div>
                </TableCell>
                {/* Colonne Commission - avec règles de visibilité */}
                {!compact && (
                    <TableCell>
                        {canViewCommission(user, prospect) ? (
                            <CommissionCell
                                prospect={prospect}
                                user={user}
                                // Perspective : quand la liste est filtrée par un user (drill-down profil),
                                // on affiche SA part propre (VP/apporteur) au lieu du total Valnexo — sinon
                                // le total header ne peut jamais matcher la somme des lignes.
                                perspectiveUser={userFilter !== 'all' ? (users.find(u => u.id === userFilter) || null) : null}
                            />
                        ) : (
                            <span className="text-xs text-slate-400">-</span>
                        )}
                    </TableCell>
                )}
                {!compact && (
                    <TableCell>
                        <div className="text-xs space-y-0.5">
                            {prospect.dossier_apporteur_name && (
                                <div className="flex items-center gap-1 text-green-700">
                                    <Users className="w-3 h-3" />
                                    <span>{prospect.dossier_apporteur_name}</span>
                                </div>
                            )}
                            {!isApporteur && prospect.dossier_vp_name && (
                                <div className="flex items-center gap-1 text-purple-700">
                                    <Crown className="w-3 h-3" />
                                    <span>{prospect.dossier_vp_name}</span>
                                </div>
                            )}
                            {prospect.team_name && (
                                <div className="flex items-center gap-1 text-slate-600">
                                    <Building2 className="w-3 h-3" />
                                    <span>{prospect.team_name}</span>
                                </div>
                            )}
                            {prospect.is_vp_direct && !prospect.team_name && (
                                <div className="flex items-center gap-1 text-amber-600 font-medium">
                                    <span className="text-xs px-1.5 py-0.5 bg-amber-100 rounded">VP Direct</span>
                                </div>
                            )}
                        </div>
                    </TableCell>
                )}
                <TableCell>
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                // Sauvegarde le contexte liste + filtres + scroll pour un retour fidèle
                                try {
                                    const ids = (listContext?.ids && listContext.ids.length ? listContext.ids : filteredProspects.map(p => p.id));
                                    saveProspectListContext(ids, prospect.id, {
                                        path: window.location.pathname + window.location.search,
                                        label: 'Retour à la liste',
                                    });
                                } catch { /* noop */ }
                                navigate(`/prospects/${prospect.id}`);
                            }}
                            data-testid={`view-prospect-${prospect.id}`}
                        >
                            <Eye className="w-4 h-4" />
                        </Button>
                        {isAdminFull && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDelete(prospect.id, e)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-prospect-${prospect.id}`}
                                title="Supprimer (réservé Admin Full)"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Encart "Dossiers audités" — séparé des dossiers normaux (visible auditeurs uniquement) */}
                <AuditedDossiersWidget />
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                            Prospects
                        </h1>
                        <p className="text-slate-600 mt-1 text-sm sm:text-base">
                            {filteredProspects.length} prospect{filteredProspects.length > 1 ? 's' : ''}
                            {stats.warningCount > 0 && (
                                <span className="ml-2 text-red-600">
                                    ({stats.warningCount} en retard)
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                                try {
                                    toast.loading('Export en cours...', { id: 'export' });
                                    const params = {};
                                    if (classFilter !== 'all') params.prospect_class = classFilter;
                                    const response = await prospectsAPI.exportExcel(params);
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `prospects_export_${new Date().toISOString().slice(0,10)}.xlsx`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Export terminé !', { id: 'export' });
                                } catch (error) {
                                    console.error('Export error:', error);
                                    toast.error('Erreur lors de l\'export', { id: 'export' });
                                }
                            }}
                            data-testid="export-excel-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Excel
                        </Button>
                        <Button
                            variant={showStats ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowStats(!showStats)}
                            data-testid="toggle-stats-btn"
                        >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Stats
                        </Button>
                        <Button
                            variant={viewMode === 'grouped' ? "default" : "outline"}
                            size="sm"
                            onClick={() => setViewMode(viewMode === 'list' ? 'grouped' : 'list')}
                            data-testid="toggle-view-btn"
                        >
                            <Layers className="w-4 h-4 mr-2" />
                            {viewMode === 'list' ? 'Par équipe' : 'Liste'}
                        </Button>
                        <Button
                            onClick={() => navigate('/prospects/new')}
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid="add-prospect-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau Prospect
                        </Button>
                    </div>
                </div>

                {/* Quick tabs : Tous / En cours / Gagnés / En retard */}
                {searchParams.get('ids') && (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="ids-filter-banner">
                        <p className="text-sm font-medium text-amber-800">
                            {searchParams.get('label') || 'Liste filtrée'} · {filteredProspects.length} dossier(s)
                        </p>
                        <button type="button" data-testid="clear-ids-filter"
                            onClick={() => { const sp = new URLSearchParams(searchParams); sp.delete('ids'); sp.delete('label'); setSearchParams(sp); }}
                            className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline">
                            Effacer le filtre
                        </button>
                    </div>
                )}
                <div className="flex flex-wrap gap-2 border-b border-slate-200 -mb-px overflow-x-auto" data-testid="quick-tabs">
                    {[
                        { id: 'all', label: 'Tous', icon: Users, accent: 'slate' },
                        { id: 'en_cours', label: 'En cours', icon: Briefcase, accent: 'blue' },
                        { id: 'attente_installation', label: "En attente d'installation", icon: PackageCheck, accent: 'indigo' },
                        { id: 'gagnes', label: 'Gagnés', icon: Trophy, accent: 'emerald' },
                        { id: 'en_retard', label: 'En retard', icon: Clock, accent: 'orange' },
                        ...(Object.keys(wallUnreadByProspect).length > 0 ? [{ id: 'unread', label: 'Messages non lus', icon: MessageSquareText, accent: 'purple', badge: wallUnreadTotal }] : []),
                    ].map(t => {
                        const TabIcon = t.icon;
                        const active = quickTab === t.id;
                        const accentMap = {
                            slate: active ? 'border-slate-900 text-slate-900' : 'text-slate-500 hover:text-slate-700',
                            blue: active ? 'border-blue-600 text-blue-700' : 'text-slate-500 hover:text-blue-700',
                            indigo: active ? 'border-indigo-600 text-indigo-700' : 'text-slate-500 hover:text-indigo-700',
                            emerald: active ? 'border-emerald-600 text-emerald-700' : 'text-slate-500 hover:text-emerald-700',
                            orange: active ? 'border-orange-500 text-orange-700' : 'text-slate-500 hover:text-orange-700',
                            purple: active ? 'border-purple-600 text-purple-700' : 'text-slate-500 hover:text-purple-700',
                        };
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setQuickTab(t.id)}
                                className={cn(
                                    "inline-flex items-center gap-2 px-4 py-2.5 border-b-2 font-medium text-sm transition-colors whitespace-nowrap",
                                    active ? accentMap[t.accent] : 'border-transparent ' + accentMap[t.accent]
                                )}
                                data-testid={`quick-tab-${t.id}`}
                            >
                                <TabIcon className="w-4 h-4" />
                                {t.label}
                                {t.badge > 0 && (
                                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-purple-600 text-white text-[10px] font-bold">
                                        {t.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Stats Cards — partition ADDITIVE : Total = En cours + En retard + Gagnés + Perdus */}
                {showStats && (
                    <div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <StatsCard 
                                title="Total dossiers" 
                                value={stats.total} 
                                icon={Users}
                                color="indigo"
                            />
                            <StatsCard 
                                title="En cours" 
                                value={stats.enCoursCount} 
                                icon={Briefcase}
                                color="blue"
                            />
                            <StatsCard 
                                title="En retard (>10j)" 
                                value={stats.warningCount} 
                                icon={AlertTriangle}
                                color="red"
                            />
                            <StatsCard 
                                title="Gagnés" 
                                value={stats.gagnesCount} 
                                icon={Trophy}
                                color="emerald"
                            />
                            <StatsCard 
                                title="Perdus" 
                                value={stats.perdusCount} 
                                icon={XCircle}
                                color="slate"
                            />
                            <StatsCard 
                                title="Taux conversion" 
                                value={`${stats.conversionRate}%`} 
                                icon={TrendingUp}
                                color="purple"
                            />
                        </div>
                        <p className="text-xs text-slate-400 mt-2" data-testid="stats-partition-hint">
                            Total = En cours + En retard + Gagnés + Perdus ({stats.enCoursCount} + {stats.warningCount} + {stats.gagnesCount} + {stats.perdusCount} = {stats.total})
                        </p>
                    </div>
                )}

                {/* Barre de recherche - Pleine largeur */}
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                        placeholder="Rechercher par nom, email, téléphone, ville, entreprise..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-12 h-12 text-base"
                        data-testid="search-input"
                    />
                </div>

                {/* Filtres - Ligne compacte */}
                <div className="flex flex-wrap gap-3 items-center">
                    {/* Filtre par période */}
                    <Select value={periodFilter} onValueChange={setPeriodFilter}>
                        <SelectTrigger className="w-[160px]" data-testid="period-filter">
                            <Calendar className="w-4 h-4 mr-2 text-slate-500" />
                            <SelectValue placeholder="Période" />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIOD_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    {/* Filtre par équipe - visible pour Admin et VP */}
                    {(isAdmin || isVP) && teams.length > 0 && (
                        <Select value={teamFilter} onValueChange={setTeamFilter}>
                            <SelectTrigger className="w-[160px]" data-testid="team-filter">
                                <Building2 className="w-4 h-4 mr-2 text-slate-500" />
                                <SelectValue placeholder="Équipe" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les équipes</SelectItem>
                                {teams.map((team) => (
                                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {/* Filtre "Périmètre" — visible pour tous les VP.
                        - "VP Direct" : dossiers que le VP gère seul (pas d'apporteur, pas de parrain)
                        - "Mon équipe" / "Équipes supervisées" : uniquement si le VP supervise
                          au moins une équipe où il n'est pas payeur */}
                    {isVP && (
                        <Select value={scopeFilter} onValueChange={setScopeFilter}>
                            <SelectTrigger className="w-[210px]" data-testid="scope-filter">
                                <Users className="w-4 h-4 mr-2 text-indigo-500" />
                                <SelectValue placeholder="Périmètre" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all" data-testid="scope-filter-all">Tous les dossiers</SelectItem>
                                <SelectItem value="vp_direct" data-testid="scope-filter-vp-direct">
                                    🎯 Mes dossiers VP direct
                                </SelectItem>
                                {teams.some(t => t.supervisor_vp_id === user?.id && t.payer_vp_id !== user?.id) && (
                                    <>
                                        <SelectItem value="mine" data-testid="scope-filter-mine">Mon équipe (VP Payeur)</SelectItem>
                                        <SelectItem value="supervised" data-testid="scope-filter-supervised">Équipes supervisées</SelectItem>
                                    </>
                                )}
                            </SelectContent>
                        </Select>
                    )}

                    {/* Filtre par utilisateur — Admin voit tous ; VP voit uniquement ses apporteurs */}
                    {(isAdmin || isVP) && users.length > 0 && (
                        <UserPickerPopover
                            users={users}
                            value={userFilter === 'all' ? null : userFilter}
                            onChange={(id) => setUserFilter(id || 'all')}
                            allowAll
                            showAllLabel={isVP ? 'Tous mes apporteurs' : 'Tous les utilisateurs'}
                            placeholder={isVP ? 'Apporteur' : 'Utilisateur'}
                            width={240}
                            triggerClassName="w-[180px]"
                            testId="user-filter"
                        />
                    )}
                    
                    <Select value={classFilter} onValueChange={setClassFilter}>
                        <SelectTrigger className="w-[150px]" data-testid="class-filter">
                            <Filter className="w-4 h-4 mr-2 text-slate-500" />
                            <SelectValue placeholder="Classe" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes les classes</SelectItem>
                            {Object.entries(CLASS_LABELS).map(([id, name]) => (
                                <SelectItem key={id} value={id}>{name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    {/* ===== Filtres Parrainage (admin & VP avec module activé) ===== */}
                    {showParrainageFilters && (
                        <>
                            <Select value={originFilter} onValueChange={setOriginFilter}>
                                <SelectTrigger className="w-[150px]" data-testid="origin-filter">
                                    <Filter className="w-4 h-4 mr-2 text-indigo-500" />
                                    <SelectValue placeholder="Origine" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes origines</SelectItem>
                                    <SelectItem value="apporteur">Apporteur</SelectItem>
                                    <SelectItem value="parrainage">Parrainage</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={parrainLevelFilter} onValueChange={setParrainLevelFilter}>
                                <SelectTrigger className="w-[150px]" data-testid="parrain-level-filter">
                                    <Filter className="w-4 h-4 mr-2 text-indigo-500" />
                                    <SelectValue placeholder="Niveau parrain" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous niveaux</SelectItem>
                                    <SelectItem value="1">Niveau 1</SelectItem>
                                    <SelectItem value="2">Niveau 2</SelectItem>
                                    <SelectItem value="3">Niveau 3</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={parrainFilter} onValueChange={setParrainFilter}>
                                <SelectTrigger className="w-[170px]" data-testid="parrain-filter">
                                    <Filter className="w-4 h-4 mr-2 text-indigo-500" />
                                    <SelectValue placeholder="Parrain" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les parrains</SelectItem>
                                    {parrainsList.map(pr => (
                                        <SelectItem key={pr.id} value={pr.id}>
                                            N{pr.parrain_level} · {pr.first_name} {pr.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </>
                    )}
                    
                    {/* Filtre par pipeline */}
                    <Select value={pipelineFilter} onValueChange={(val) => {
                        setPipelineFilter(val);
                        setStageFilter('all'); // Reset stage filter when pipeline changes
                    }}>
                        <SelectTrigger className="w-full md:w-48" data-testid="pipeline-filter">
                            <Layers className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Pipeline" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les pipelines</SelectItem>
                            <SelectItem value="easy_power">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-amber-500" />
                                    Courtage
                                </div>
                            </SelectItem>
                            <SelectItem value="chantier">
                                <div className="flex items-center gap-2">
                                    <HardHat className="w-4 h-4 text-blue-500" />
                                    Travaux d’optimisation
                                </div>
                            </SelectItem>
                            <SelectItem value="lost">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-500" />
                                    Perdus
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    
                    {/* Filtre par étape - dépend du pipeline sélectionné */}
                    <Select value={stageFilter} onValueChange={setStageFilter}>
                        <SelectTrigger className="w-full md:w-52" data-testid="stage-filter">
                            <BarChart3 className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Étape" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes les étapes</SelectItem>
                            {/* Étapes Courtage */}
                            {(pipelineFilter === 'all' || pipelineFilter === 'easy_power') && (
                                <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-amber-600 bg-amber-50">
                                        <Zap className="w-3 h-3 inline mr-1" />
                                        Courtage
                                    </div>
                                    {Object.entries(EASY_POWER_STAGE_NAMES).map(([id, name]) => (
                                        <SelectItem key={id} value={id}>
                                            <span className="pl-2">{name}</span>
                                        </SelectItem>
                                    ))}
                                    <SelectItem value="ep_perdu">
                                        <span className="pl-2 text-red-600">Perdu</span>
                                    </SelectItem>
                                </>
                            )}
                            {/* Étapes Travaux d’optimisation */}
                            {(pipelineFilter === 'all' || pipelineFilter === 'chantier') && (
                                <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50">
                                        <HardHat className="w-3 h-3 inline mr-1" />
                                        Travaux d’optimisation
                                    </div>
                                    {Object.entries(CHANTIER_STAGE_NAMES).map(([id, name]) => (
                                        <SelectItem key={id} value={id}>
                                            <span className="pl-2">{name}</span>
                                        </SelectItem>
                                    ))}
                                    <SelectItem value="ch_perdu">
                                        <span className="pl-2 text-red-600">Perdu</span>
                                    </SelectItem>
                                </>
                            )}
                        </SelectContent>
                    </Select>
                    
                    {/* Filtre par commission - nouveau */}
                    <Select value={commissionFilter} onValueChange={setCommissionFilter}>
                        <SelectTrigger className="w-full md:w-48" data-testid="commission-filter">
                            <CircleDollarSign className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Commission" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes commissions</SelectItem>
                            <SelectItem value="a_chercher">
                                <span className="flex items-center gap-1 text-amber-600">
                                    <Target className="w-3 h-3" />
                                    À aller chercher
                                </span>
                            </SelectItem>
                            <SelectItem value="validee">
                                <span className="flex items-center gap-1 text-emerald-600">
                                    <CheckCircle className="w-3 h-3" />
                                    Validées
                                </span>
                            </SelectItem>
                            <SelectItem value="avec_commission">Avec commission</SelectItem>
                            <SelectItem value="sans_commission">Sans commission</SelectItem>
                        </SelectContent>
                    </Select>
                    
                    {/* Filtre par statut (en retard, perdu) */}
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full md:w-44" data-testid="status-filter">
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            <SelectItem value="actif">
                                <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle className="w-3 h-3" />
                                    Actifs
                                </span>
                            </SelectItem>
                            <SelectItem value="en_retard">
                                <span className="flex items-center gap-1 text-amber-600">
                                    <Clock className="w-3 h-3" />
                                    En retard (&gt;5j)
                                </span>
                            </SelectItem>
                            <SelectItem value="perdu">
                                <span className="flex items-center gap-1 text-red-600">
                                    <XCircle className="w-3 h-3" />
                                    Perdus
                                </span>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Filtre par hashtags # */}
                    <HashtagFilter
                        selected={hashtagFilter}
                        onChange={setHashtagFilter}
                        className="w-full md:w-44"
                    />
                    {hashtagFilter.length > 0 && (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700"
                            data-testid="hashtag-match-count"
                        >
                            <Hash className="w-3 h-3" />{hashtagMatchCount} dossier{hashtagMatchCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {/* Résumé des filtres actifs et compteur */}
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-slate-600">
                        <strong>{filteredProspects.length}</strong> dossier{filteredProspects.length !== 1 ? 's' : ''} 
                        {filteredProspects.length !== prospects.length && (
                            <span className="text-slate-400"> sur {prospects.length}</span>
                        )}
                    </span>
                    
                    {/* Badges des filtres actifs */}
                    {(teamFilter !== 'all' || userFilter !== 'all' || classFilter !== 'all' || pipelineFilter !== 'all' || stageFilter !== 'all' || periodFilter !== 'all' || commissionFilter !== 'all' || statusFilter !== 'all' || search) && (
                        <>
                            <span className="text-slate-300">|</span>
                            <div className="flex flex-wrap gap-1.5">
                                {search && (
                                    <Badge variant="secondary" className="text-xs">
                                        Recherche: "{search}"
                                    </Badge>
                                )}
                                {teamFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs bg-slate-100">
                                        <Building2 className="w-3 h-3 mr-1" />
                                        {teams.find(t => t.id === teamFilter)?.name || teamFilter}
                                    </Badge>
                                )}
                                {userFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs bg-slate-100">
                                        <Users className="w-3 h-3 mr-1" />
                                        {users.find(u => u.id === userFilter)?.first_name || userFilter}
                                    </Badge>
                                )}
                                {classFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs bg-slate-100">
                                        {CLASS_LABELS[classFilter]}
                                    </Badge>
                                )}
                                {pipelineFilter !== 'all' && (
                                    <Badge variant="secondary" className={cn("text-xs", 
                                        pipelineFilter === 'easy_power' && "bg-amber-100 text-amber-700",
                                        pipelineFilter === 'chantier' && "bg-blue-100 text-blue-700",
                                        pipelineFilter === 'lost' && "bg-red-100 text-red-700"
                                    )}>
                                        {pipelineFilter === 'easy_power' && <><Zap className="w-3 h-3 mr-1" />Courtage</>}
                                        {pipelineFilter === 'chantier' && <><HardHat className="w-3 h-3 mr-1" />Travaux d’optimisation</>}
                                        {pipelineFilter === 'lost' && <><AlertTriangle className="w-3 h-3 mr-1" />Perdus</>}
                                    </Badge>
                                )}
                                {stageFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                        <BarChart3 className="w-3 h-3 mr-1" />
                                        {EASY_POWER_STAGE_NAMES[stageFilter] || CHANTIER_STAGE_NAMES[stageFilter] || stageFilter}
                                    </Badge>
                                )}
                                {periodFilter !== 'all' && (
                                    <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">
                                        <Calendar className="w-3 h-3 mr-1" />
                                        {PERIOD_OPTIONS.find(p => p.value === periodFilter)?.label}
                                    </Badge>
                                )}
                                {commissionFilter !== 'all' && (
                                    <Badge variant="secondary" className={cn("text-xs", 
                                        commissionFilter === 'a_chercher' && "bg-amber-100 text-amber-700",
                                        commissionFilter === 'validee' && "bg-emerald-100 text-emerald-700",
                                        (commissionFilter === 'avec_commission' || commissionFilter === 'sans_commission') && "bg-purple-100 text-purple-700"
                                    )}>
                                        <CircleDollarSign className="w-3 h-3 mr-1" />
                                        {commissionFilter === 'a_chercher' && 'À aller chercher'}
                                        {commissionFilter === 'validee' && 'Validées'}
                                        {commissionFilter === 'avec_commission' && 'Avec commission'}
                                        {commissionFilter === 'sans_commission' && 'Sans commission'}
                                    </Badge>
                                )}
                                {statusFilter !== 'all' && (
                                    <Badge variant="secondary" className={cn("text-xs", 
                                        statusFilter === 'actif' && "bg-green-100 text-green-700",
                                        statusFilter === 'en_retard' && "bg-amber-100 text-amber-700",
                                        statusFilter === 'perdu' && "bg-red-100 text-red-700"
                                    )}>
                                        {statusFilter === 'actif' && <><CheckCircle className="w-3 h-3 mr-1" />Actifs</>}
                                        {statusFilter === 'en_retard' && <><Clock className="w-3 h-3 mr-1" />En retard</>}
                                        {statusFilter === 'perdu' && <><XCircle className="w-3 h-3 mr-1" />Perdus</>}
                                    </Badge>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSearch('');
                                    setTeamFilter('all');
                                    setUserFilter('all');
                                    setClassFilter('all');
                                    setPipelineFilter('all');
                                    setStageFilter('all');
                                    setPeriodFilter('all');
                                    setCommissionFilter('all');
                                    setStatusFilter('all');
                                    setSearchParams({}, { replace: true });
                                }}
                                className="text-xs text-slate-500 hover:text-slate-700"
                                data-testid="reset-filters-btn"
                            >
                                Réinitialiser
                            </Button>
                        </>
                    )}
                </div>

                {/* Vue groupée par équipe */}
                {viewMode === 'grouped' ? (
                    <div className="space-y-4">
                        {Object.entries(groupedData).map(([teamId, teamData]) => (
                            <Card key={teamId} className="overflow-hidden">
                                <Collapsible open={expandedTeams[teamId] !== false} onOpenChange={() => toggleTeam(teamId)}>
                                    <CollapsibleTrigger asChild>
                                        <CardHeader className={cn(
                                            "cursor-pointer hover:bg-slate-50 transition-colors py-3",
                                            teamData.warningCount > 0 && "bg-red-50/50"
                                        )}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    {expandedTeams[teamId] !== false ? (
                                                        <ChevronDown className="w-5 h-5 text-slate-400" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 text-slate-400" />
                                                    )}
                                                    <Building2 className="w-5 h-5 text-slate-600" />
                                                    <CardTitle className="text-lg">{teamData.name}</CardTitle>
                                                    <Badge variant="secondary">{teamData.totalProspects} dossiers</Badge>
                                                    {teamData.warningCount > 0 && (
                                                        <Badge variant="destructive" className="bg-red-100 text-red-700">
                                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                                            {teamData.warningCount} en retard
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </CardHeader>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <CardContent className="pt-0 pb-4">
                                            <div className="space-y-3">
                                                {Object.entries(teamData.users).map(([userId, userData]) => (
                                                    <Collapsible 
                                                        key={userId} 
                                                        open={expandedUsers[`${teamId}-${userId}`] !== false}
                                                        onOpenChange={() => toggleUser(teamId, userId)}
                                                    >
                                                        <CollapsibleTrigger asChild>
                                                            <div className={cn(
                                                                "flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors",
                                                                userData.warningCount > 0 && "bg-red-50"
                                                            )}>
                                                                <div className="flex items-center gap-3">
                                                                    {expandedUsers[`${teamId}-${userId}`] !== false ? (
                                                                        <ChevronDown className="w-4 h-4 text-slate-400" />
                                                                    ) : (
                                                                        <ChevronRight className="w-4 h-4 text-slate-400" />
                                                                    )}
                                                                    {userData.role === 'vp' ? (
                                                                        <Crown className="w-4 h-4 text-purple-600" />
                                                                    ) : userData.role === 'apporteur' ? (
                                                                        <Users className="w-4 h-4 text-green-600" />
                                                                    ) : (
                                                                        <Users className="w-4 h-4 text-slate-400" />
                                                                    )}
                                                                    <span className="font-medium">{userData.name}</span>
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {userData.prospects.length} dossiers
                                                                    </Badge>
                                                                    {userData.warningCount > 0 && (
                                                                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                                                            {userData.warningCount} en retard
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </CollapsibleTrigger>
                                                        <CollapsibleContent>
                                                            <div className="ml-8 mt-2">
                                                                <Table>
                                                                    <TableHeader>
                                                                        <TableRow className="bg-slate-50">
                                                                            <TableHead className="font-semibold">Nom</TableHead>
                                                                            <TableHead className="font-semibold">Étape / Temps</TableHead>
                                                                            <TableHead className="font-semibold text-right">Actions</TableHead>
                                                                        </TableRow>
                                                                    </TableHeader>
                                                                    <TableBody>
                                                                        {userData.prospects.map((prospect) => (
                                                                            <ProspectRow key={prospect.id} prospect={prospect} compact />
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </div>
                                                        </CollapsibleContent>
                                                    </Collapsible>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </CollapsibleContent>
                                </Collapsible>
                            </Card>
                        ))}
                        
                        {Object.keys(groupedData).length === 0 && (
                            <Card>
                                <CardContent className="py-12 text-center text-slate-500">
                                    Aucun prospect trouvé
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : (
                    /* Vue liste classique */
                    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50">
                                    <TableHead className="font-semibold">Nom</TableHead>
                                    <TableHead className="font-semibold">Contact</TableHead>
                                    <TableHead className="font-semibold">Ville</TableHead>
                                    <TableHead className="font-semibold">Étape / Temps</TableHead>
                                    <TableHead className="font-semibold">Commission</TableHead>
                                    <TableHead className="font-semibold">Propriété</TableHead>
                                    <TableHead className="font-semibold text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredProspects.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                                            Aucun prospect trouvé
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredProspects.map((prospect) => (
                                        <ProspectRow key={prospect.id} prospect={prospect} />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {/* Légende */}
                <div className="flex items-center gap-6 text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span>Modifié récemment (&lt;24h)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span>En retard (&gt;10 jours sans avancement)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 text-xs">Xj</span>
                        <span>Jours dans l'étape</span>
                    </div>
                </div>
            </div>

            <DeleteProspectDialog
                open={deleteDialog.open}
                onOpenChange={(open) => !open && setDeleteDialog({ open: false, prospect: null })}
                prospectName={prospectDisplayName(deleteDialog.prospect)}
                processing={deleteProcessing}
                onConfirm={confirmDelete}
                mode="trash"
            />
        </DashboardLayout>
    );
}
